import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';
import { ResponsiveLeavePicker } from '../components/GanttLeavePicker';

/**
 * Self Service → Leave. Dashboard + Apply + My Requests + Approvals +
 * Holidays. Approvers see their inbox; HR users see the "HR" tab.
 */
export default function LeaveESS({ currentUser }) {
  const [tab, setTab] = useState('dashboard');
  const [types, setTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [whosOut, setWhosOut] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);

  const year = new Date().getFullYear();

  const refresh = useCallback(async () => {
    try {
      const [balRes, reqRes, outRes, holEffRes, hrsRes, apprRes] = await Promise.all([
        apiFetch(`/api/leave/balances?year=${year}`),
        apiFetch(`/api/leave/requests?mine=1`),
        apiFetch(`/api/leave/whos-out`),
        apiFetch(`/api/leave/effective/holidays?year=${year}`),
        apiFetch(`/api/leave/effective/hours`),
        apiFetch(`/api/leave/requests?pendingFor=1`),
      ]);
      if (balRes.ok)  setBalances((await balRes.json()).balances || []);
      if (reqRes.ok)  setMyRequests((await reqRes.json()).requests || []);
      if (outRes.ok)  setWhosOut((await outRes.json()).on_leave || []);
      if (hrsRes.ok)  setShift((await hrsRes.json()).shift || null);
      if (apprRes.ok) setPendingApprovals((await apprRes.json()).requests || []);

      // Holidays: prefer the employee-scoped "effective" list; if that 404s
      // because the user isn't linked to an employee record, or returns an
      // empty list, fall back to the full tenant holiday list so the page
      // isn't just "No holidays configured".
      let holidayList = [];
      if (holEffRes.ok) holidayList = (await holEffRes.json()).holidays || [];
      if (holidayList.length === 0) {
        try {
          const allHols = await apiFetch(`/api/leave/holidays?year=${year}`);
          if (allHols.ok) holidayList = (await allHols.json()).holidays || [];
        } catch (_) { /* ignore */ }
      }
      setHolidays(holidayList);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [year]);

  useEffect(() => {
    (async () => {
      try {
        const t = await apiFetch('/api/leave/types');
        if (t.ok) setTypes((await t.json()).types || []);
      } catch (_) {}
    })();
    refresh();
  }, [refresh]);

  /* ───────── Apply form state ───────── */
  const [form, setForm] = useState({
    leave_type_id: '', from_date: '', to_date: '', is_half_day: false,
    half_day_period: 'AM', reason: '', document_url: '',
    rh_holiday_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  /* Identify whether the currently-selected leave type is Restricted Holiday
   * (code = 'RH'). When it is, we show a dropdown of the year's restricted
   * holidays, hide free-form date pickers, and enforce the policy quota. */
  const selectedType = useMemo(() => {
    if (!form.leave_type_id) return null;
    const b = balances.find(x => x.leave_type.id === form.leave_type_id);
    if (b) return b.leave_type;
    return types.find(t => t.id === form.leave_type_id) || null;
  }, [form.leave_type_id, balances, types]);
  const isRH = selectedType && String(selectedType.code || '').toUpperCase() === 'RH';
  const isWFH = selectedType && String(selectedType.code || '').toUpperCase() === 'WFH';
  // Defensive accessor — some balance rows may arrive with no leave_type join
  // (e.g. orphaned row after a type was deactivated); don't crash the page.
  const codeOf = (b) => String((b && b.leave_type && b.leave_type.code) || '').toUpperCase();

  const rhBalance = useMemo(
    () => balances.find(b => codeOf(b) === 'RH'),
    [balances]
  );
  /** Split WFH out of the leave catalogue so it renders as its own tile /
   *  button rather than inside the standard balance grid. */
  const wfhBalance = useMemo(
    () => balances.find(b => codeOf(b) === 'WFH'),
    [balances]
  );
  const leaveBalances = useMemo(
    () => balances.filter(b => b && b.leave_type && codeOf(b) !== 'WFH'),
    [balances]
  );
  /** How many WFH days the employee has already booked in this calendar year
   *  (pending + approved). We don't show "used of X" because WFH has no fixed
   *  quota — just surface the running count. */
  const wfhUsedThisYear = useMemo(() => {
    const yr = new Date().getFullYear();
    return myRequests
      .filter(r => String(r.leave_type_code || '').toUpperCase() === 'WFH')
      .filter(r => ['pending','approved','auto_approved'].includes(r.status))
      .filter(r => new Date(r.from_date).getFullYear() === yr)
      .reduce((s, r) => s + (parseFloat(r.days_count) || 0), 0);
  }, [myRequests]);

  /** Open Apply tab and pre-select WFH. */
  function startWFHApply() {
    const wfhType = wfhBalance ? wfhBalance.leave_type : types.find(t => String(t.code).toUpperCase() === 'WFH');
    if (!wfhType) { toast.error('WFH is not configured — HR can add it under HCM → Leave Configurator'); return; }
    setForm(f => ({ ...f, leave_type_id: wfhType.id }));
    setTab('apply');
  }
  /** Holidays tagged 'restricted' that haven't already been consumed by this
   *  user's pending / approved RH requests in the same calendar year. */
  const availableRH = useMemo(() => {
    const alreadyUsedDates = new Set(
      myRequests
        .filter(r => ['pending','approved','auto_approved'].includes(r.status)
                  && String(r.leave_type_code || '').toUpperCase() === 'RH')
        .map(r => String(r.from_date).slice(0, 10))
    );
    return (holidays || [])
      .filter(h => String(h.category).toLowerCase() === 'restricted')
      .filter(h => !alreadyUsedDates.has(String(h.holiday_date).slice(0, 10)))
      .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date));
  }, [holidays, myRequests]);

  async function submit(e) {
    e.preventDefault();

    // Restricted Holiday guard: must pick a specific RH date from the dropdown,
    // and the user must still have quota left.
    if (isRH) {
      if (!form.rh_holiday_id) { toast.error('Pick a restricted holiday from the list'); return; }
      if (rhBalance && rhBalance.available <= 0) {
        toast.error(`You've used your quota of ${rhBalance.allotted} Restricted Holiday(s) for the year`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // Strip the UI-only rh_holiday_id before posting — server only needs
      // the date tuple. It validates quota server-side too.
      const { rh_holiday_id, ...payload } = form;
      const r = await apiFetch('/api/leave/requests', { method: 'POST', body: JSON.stringify(payload) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Submitted ${d.days_count} day(s) · ${d.status === 'auto_approved' ? 'auto-approved' : 'pending approval'}`);
        setForm({ leave_type_id: '', from_date: '', to_date: '', is_half_day: false, half_day_period: 'AM', reason: '', document_url: '', rh_holiday_id: '' });
        refresh();
        setTab('requests');
      } else {
        toast.error(d.error || 'Could not submit');
      }
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  }

  async function decide(id, action, reason) {
    try {
      const r = await apiFetch(`/api/leave/requests/${id}/${action}`, {
        method: 'PUT',
        body: JSON.stringify(reason ? { reason } : {}),
      });
      if (r.ok) { toast.success(action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Cancelled'); refresh(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Failed'); }
    } catch (e) { toast.error(e.message); }
  }

  /* ── Edit an existing request ── */
  const [editReq, setEditReq] = useState(null);
  const [editForm, setEditForm] = useState(null);
  function openEdit(r) {
    setEditReq(r);
    setEditForm({
      leave_type_id: r.leave_type_id,
      from_date: String(r.from_date).slice(0, 10),
      to_date: String(r.to_date).slice(0, 10),
      is_half_day: !!r.is_half_day,
      half_day_period: r.half_day_period || 'AM',
      reason: r.reason || '',
      document_url: r.document_url || '',
    });
  }
  async function submitEdit() {
    try {
      const r = await apiFetch(`/api/leave/requests/${editReq.id}`, {
        method: 'PUT', body: JSON.stringify(editForm),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(d.message || 'Updated');
        setEditReq(null); setEditForm(null);
        refresh();
      } else { toast.error(d.error || 'Edit failed'); }
    } catch (e) { toast.error(e.message); }
  }

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
  const fullName = (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim();

  const firstName = currentUser?.firstName || currentUser?.fullName?.split(' ')[0] || 'there';
  // Gender of the current user (sourced from /auth/me → employees row).
  // Used to filter leave types whose `gender_restriction` does not match.
  const myGender = (currentUser?.gender || '').trim();
  function leaveAllowedForMe(lt) {
    const g = (lt && lt.gender_restriction) || 'All';
    if (!g || g === 'All') return true;
    if (!myGender) return true;            // unknown gender → don't hide anything
    return g === myGender;
  }

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          ['dashboard', 'Dashboard'],
          ['apply',     'Apply Leave'],
          ['requests',  'My Requests'],
          ['approvals', `Approvals${pendingApprovals.length ? ' · ' + pendingApprovals.length : ''}`],
          ['holidays',  'Holidays'],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '7px 16px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
            border: '1px solid ' + (tab === k ? 'rgba(191,95,255,0.45)' : 'rgba(255,255,255,0.08)'),
            background: tab === k ? 'linear-gradient(135deg, rgba(255,0,229,0.18), rgba(191,95,255,0.18))' : 'transparent',
            color: tab === k ? '#fff' : '#94A3B8',
          }}>{lbl}</button>
        ))}
      </div>

      {/* ─────────── DASHBOARD ─────────── */}
      {tab === 'dashboard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={cardStyle}>
            <div style={{ color: '#94A3B8', fontSize: 13 }}>
              {shift ? `${shift.name} · ${shift.shift_start} – ${shift.shift_end}` : 'No shift configured'} · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <h1 style={{ margin: '6px 0 10px', fontSize: 22 }}>Welcome {firstName}</h1>
            <div style={{ color: '#94A3B8', fontSize: 13, maxWidth: 720 }}>
              Apply for leave from here. Approvers and HR are routed automatically from your Org Structure and Group Tag.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button onClick={() => setTab('apply')} style={btnPrimary}>Apply Leave</button>
              {/* Apply WFH is intentionally a separate CTA with its own colour
                  so it doesn't look like another leave type — HR policy treats
                  WFH as a work-arrangement request, not a leave consumption. */}
              <button onClick={startWFHApply} style={btnWFH}>🏠 Apply WFH</button>
              <button onClick={() => setTab('holidays')} style={btnGhost}>View Holidays</button>
            </div>
          </div>

          {/* Balances */}
          <div>
            <div style={sectionH}>Your Balances · {year}</div>
            {loading ? <div style={{ color: '#94A3B8', fontSize: 13, padding: 14 }}>Loading…</div> :
              leaveBalances.length === 0 && !wfhBalance ? <div style={emptyBox}>No leave types configured yet.</div> :
              // Compact one-row layout: chips wrap only if the screen is too
              // narrow. Each tile is now ~120px wide and shorter so the user
              // sees all leave types at a glance without scanning.
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {wfhBalance && (
                  <div style={{ ...wfhTileStyle, padding: 10, minWidth: 130, maxWidth: 160 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ ...chipStyle, background: 'rgba(251,191,36,0.18)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.45)', fontSize: 10 }}>WFH</span>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, color: '#FBBF24' }}>{wfhUsedThisYear}</div>
                    <div style={{ fontSize: 10, color: '#FBBF24', opacity: 0.85 }}>day{wfhUsedThisYear === 1 ? '' : 's'} applied</div>
                  </div>
                )}
                {leaveBalances.filter(b => leaveAllowedForMe(b.leave_type)).map(b => (
                  <div key={b.leave_type.id} style={{ ...balCard, padding: 10, minWidth: 130, maxWidth: 160 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ ...chipStyle, background: b.leave_type.color + '22', color: b.leave_type.color, border: `1px solid ${b.leave_type.color}55`, fontSize: 10 }}>{b.leave_type.code}</span>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{b.available}</div>
                    <div style={{ fontSize: 10, color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>of {b.allotted} · {b.leave_type.name}</div>
                    <div style={{ marginTop: 6, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99 }}>
                      <div style={{ height: '100%', width: `${b.allotted > 0 ? Math.min(100, (b.used + b.pending) / b.allotted * 100) : 0}%`, background: b.leave_type.color, borderRadius: 99 }}></div>
                    </div>
                    <div style={{ fontSize: 9.5, color: '#64748B', marginTop: 3 }}>U {b.used} · P {b.pending}</div>
                  </div>
                ))}
              </div>
            }
          </div>

          {/* Who's out */}
          <div>
            <div style={sectionH}>Who's Out Today</div>
            {whosOut.length === 0 ? <div style={emptyBox}>No one's on leave today.</div> :
              <div style={cardStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                  {whosOut.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={avatarStyle(p)}>{initials(p)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#E5E7EB' }}>{fullName(p)}</div>
                        <div style={{ fontSize: 11, color: '#64748B' }}>{p.department_name || '—'} · {fmtDate(p.from_date)}–{fmtDate(p.to_date)}</div>
                      </div>
                      <span style={{ ...chipStyle, background: (p.color || '#94A3B8') + '22', color: p.color || '#94A3B8', border: `1px solid ${(p.color || '#94A3B8')}55` }}>{p.leave_type_code}</span>
                    </div>
                  ))}
                </div>
              </div>
            }
          </div>
        </div>
      )}

      {/* ─────────── APPLY LEAVE ─────────── */}
      {tab === 'apply' && (
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 16px', fontSize: 17 }}>Apply for Leave</h2>
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <Field label="Leave Type">
              <select value={form.leave_type_id} onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))} style={inputStyle} required>
                <option value="">Select type…</option>
                {/* Prefer balances (shows "X avail") when available, else fall
                    back to the flat types catalog so the dropdown is never empty
                    — this happens when the user isn't linked to an employee
                    record yet, which would otherwise 404 the balances API. */}
                {/* WFH is hidden from this dropdown — it has its own dedicated
                    "Apply WFH" button on the dashboard and in the greeting card.
                    We still allow it in the form if `isWFH` is true (set when
                    startWFHApply() pre-selects it). */}
                {(balances.length > 0
                  ? balances
                      .filter(b => isWFH || String(b.leave_type.code).toUpperCase() !== 'WFH')
                      .filter(b => leaveAllowedForMe(b.leave_type))
                      .map(b => ({ id: b.leave_type.id, name: b.leave_type.name, hint: `${b.available} avail` }))
                  : types
                      .filter(t => isWFH || String(t.code).toUpperCase() !== 'WFH')
                      .filter(t => leaveAllowedForMe(t))
                      .map(t => ({ id: t.id, name: t.name, hint: t.code }))
                ).map(o => (
                  <option key={o.id} value={o.id}>{o.name}{o.hint ? ` · ${o.hint}` : ''}</option>
                ))}
              </select>
              {balances.length === 0 && types.length === 0 && (
                <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>
                  No leave types configured yet. HR can add them under HCM → Leave Configurator.
                </div>
              )}
            </Field>
            {isRH ? (
              <Field label="Restricted Holiday" wide>
                <select
                  value={form.rh_holiday_id}
                  onChange={e => {
                    const id = e.target.value;
                    const h = availableRH.find(x => x.id === id);
                    const d = h ? String(h.holiday_date).slice(0, 10) : '';
                    setForm(f => ({ ...f, rh_holiday_id: id, from_date: d, to_date: d }));
                  }}
                  style={inputStyle}
                  required
                >
                  <option value="">Select a restricted holiday…</option>
                  {availableRH.map(h => (
                    <option key={h.id} value={h.id}>
                      {new Date(h.holiday_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' })} · {h.name}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: rhBalance && rhBalance.available <= 0 ? '#FCA5A5' : '#94A3B8', marginTop: 4 }}>
                  {rhBalance
                    ? `${rhBalance.available} of ${rhBalance.allotted} restricted holiday(s) remaining this year`
                    : 'Quota enforced by policy'}
                  {availableRH.length === 0 && rhBalance && rhBalance.available > 0 && (
                    <span> · No upcoming restricted holidays in the calendar</span>
                  )}
                </div>
              </Field>
            ) : (
              <Field label="Duration" wide>
                <ResponsiveLeavePicker
                  fromDate={form.from_date}
                  toDate={form.to_date}
                  onChange={({ from_date, to_date }) => setForm(f => ({ ...f, from_date, to_date }))}
                  holidays={holidays}
                  shift={shift}
                />
              </Field>
            )}
            {!isRH && (
              <Field label="Half-day">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 6 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#E5E7EB' }}>
                    <input type="checkbox" checked={form.is_half_day} onChange={e => setForm(f => ({ ...f, is_half_day: e.target.checked }))} /> Half
                  </label>
                  {form.is_half_day && (
                    <select value={form.half_day_period} onChange={e => setForm(f => ({ ...f, half_day_period: e.target.value }))} style={{ ...inputStyle, width: 80 }}>
                      <option>AM</option><option>PM</option>
                    </select>
                  )}
                </div>
              </Field>
            )}
            <Field label="Document URL (if required)" wide>
              <input value={form.document_url} onChange={e => setForm(f => ({ ...f, document_url: e.target.value }))} style={inputStyle} placeholder="Link to uploaded medical certificate, etc." />
            </Field>
            <Field label="Reason" wide>
              <textarea rows={2} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Short reason (optional)" />
            </Field>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setTab('dashboard')} style={btnGhost}>Cancel</button>
              <button type="submit" disabled={submitting} style={btnPrimary}>{submitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </form>
        </div>
      )}

      {/* ─────────── MY REQUESTS ─────────── */}
      {tab === 'requests' && (
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 16px', fontSize: 17 }}>My Leave Requests</h2>
          {myRequests.length === 0 ? <div style={emptyBox}>No requests yet. Click <strong>Apply Leave</strong>.</div> :
            <div style={{ overflowX: 'auto' }}>
              <table style={tblStyle}>
                <thead>
                  <tr>
                    <Th>Type</Th><Th>From → To</Th><Th align="right">Days</Th><Th>Reason</Th><Th>Approver</Th><Th>Status</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {myRequests.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <Td><span style={{ ...chipStyle, background: (r.leave_type_color || '#94A3B8') + '22', color: r.leave_type_color || '#94A3B8' }}>{r.leave_type_code}</span></Td>
                      <Td>{fmtDate(r.from_date)} → {fmtDate(r.to_date)}</Td>
                      <Td align="right">{r.days_count}</Td>
                      <Td style={{ color: '#94A3B8' }}>{r.reason || '—'}</Td>
                      <Td>{r.approver_first_name ? `${r.approver_first_name} ${r.approver_last_name || ''}` : '—'}</Td>
                      <Td><StatusChip status={r.status} /></Td>
                      <Td>{['pending', 'approved', 'auto_approved'].includes(r.status) && (
                        <span style={{ display: 'inline-flex', gap: 10 }}>
                          <button onClick={() => openEdit(r)} style={linkBtn}>Edit</button>
                          {/* Self-cancel only allowed up to 1 day before start.
                              Past that, only HR can cancel — we hide the link
                              and the user can ask HR. */}
                          {(() => {
                            const start = new Date(r.from_date);
                            const cutoff = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                            cutoff.setDate(cutoff.getDate() - 1);
                            return Date.now() <= cutoff.getTime() ? (
                              <button onClick={() => decide(r.id, 'cancel')} style={linkBtn}>Cancel</button>
                            ) : (
                              <span style={{ fontSize: 11, color: '#64748B' }} title="Cancel window passed — ask HR">Ask HR to cancel</span>
                            );
                          })()}
                        </span>
                      )}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </div>
      )}

      {/* ─────────── APPROVALS ─────────── */}
      {tab === 'approvals' && (
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 16px', fontSize: 17 }}>Approvals Inbox</h2>
          {pendingApprovals.length === 0 ? <div style={emptyBox}>No pending approvals.</div> :
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pendingApprovals.map(r => (
                <div key={r.id} style={{ ...cardStyle, padding: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={avatarStyle(r, 44)}>{initials(r)}</div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{fullName(r)}</div>
                    <div style={{ fontSize: 11.5, color: '#94A3B8' }}>
                      {r.office_name || '—'} · {r.group_tag_name || 'no tag'}
                    </div>
                    <div style={{ fontSize: 13, color: '#E5E7EB', marginTop: 6 }}>
                      <span style={{ ...chipStyle, background: (r.leave_type_color || '#94A3B8') + '22', color: r.leave_type_color || '#94A3B8', marginRight: 6 }}>{r.leave_type_code}</span>
                      {fmtDate(r.from_date)} → {fmtDate(r.to_date)} · {r.days_count} day(s)
                    </div>
                    {r.reason && <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic', marginTop: 4 }}>"{r.reason}"</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { const reason = window.prompt('Reason for rejection?'); if (reason !== null) decide(r.id, 'reject', reason); }} style={btnDanger}>Reject</button>
                    <button onClick={() => decide(r.id, 'approve')} style={btnApprove}>Approve</button>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}

      {/* ─────────── HOLIDAYS ─────────── */}
      {tab === 'holidays' && (
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 16px', fontSize: 17 }}>Your Holiday Calendar · {year}</h2>
          {holidays.length === 0 ? <div style={emptyBox}>No holidays configured.</div> :
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {holidays.map(h => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 8, background: holidayBg(h.category), display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `1px solid ${holidayBorder(h.category)}` }}>
                    <div style={{ fontSize: 9, textTransform: 'uppercase', color: holidayBorder(h.category) }}>{monthShort(h.holiday_date)}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: holidayBorder(h.category), lineHeight: 1 }}>{dayOfMonth(h.holiday_date)}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{h.name}</div>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{new Date(h.holiday_date).toLocaleDateString('en-IN', { weekday: 'short' })} · {h.category}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}

      {/* ── Edit Request modal ── */}
      {editReq && editForm && (
        <div onClick={() => setEditReq(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0B1220', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Edit Leave Request</h3>
              {(editReq.status === 'approved' || editReq.status === 'auto_approved') && (
                <span style={{ fontSize: 11, color: '#FBC56B', background: 'rgba(245,158,11,0.14)', padding: '3px 9px', borderRadius: 999 }}>
                  Already approved — editing will send it back for re-approval
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Leave type">
                <select value={editForm.leave_type_id} onChange={e => setEditForm(f => ({ ...f, leave_type_id: e.target.value }))} style={inputStyle}>
                  {(types.length ? types : balances.map(b => b.leave_type)).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Half-day">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 6 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={editForm.is_half_day} onChange={e => setEditForm(f => ({ ...f, is_half_day: e.target.checked }))} /> Half
                  </label>
                  {editForm.is_half_day && (
                    <select value={editForm.half_day_period} onChange={e => setEditForm(f => ({ ...f, half_day_period: e.target.value }))} style={{ ...inputStyle, width: 80 }}>
                      <option>AM</option><option>PM</option>
                    </select>
                  )}
                </div>
              </Field>
              <Field label="From date">
                <input type="date" value={editForm.from_date} onChange={e => setEditForm(f => ({ ...f, from_date: e.target.value }))} style={inputStyle} />
              </Field>
              <Field label="To date">
                <input type="date" value={editForm.to_date} onChange={e => setEditForm(f => ({ ...f, to_date: e.target.value }))} style={inputStyle} />
              </Field>
              <Field label="Document URL" wide>
                <input value={editForm.document_url} onChange={e => setEditForm(f => ({ ...f, document_url: e.target.value }))} style={inputStyle} />
              </Field>
              <Field label="Reason" wide>
                <textarea rows={2} value={editForm.reason} onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }} />
              </Field>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setEditReq(null)} style={btnGhost}>Cancel</button>
              <button onClick={submitEdit} style={btnPrimary}>Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Small helpers / styles ── */
function Field({ label, children, wide }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <span style={{ fontSize: 11, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}
function Th({ children, align }) { return <th style={{ textAlign: align || 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: '0.4px', padding: '8px 10px' }}>{children}</th>; }
function Td({ children, align, style }) { return <td style={{ fontSize: 13, color: '#E5E7EB', padding: '10px', textAlign: align || 'left', ...(style || {}) }}>{children}</td>; }
function StatusChip({ status }) {
  const color = status === 'approved' || status === 'auto_approved' ? '#10B981'
              : status === 'rejected' ? '#EF4444'
              : status === 'cancelled' ? '#94A3B8'
              : '#F59E0B';
  return <span style={{ ...chipStyle, background: color + '22', color, border: `1px solid ${color}55` }}>{status.replace('_', ' ')}</span>;
}
function avatarStyle(p, size = 32) {
  if (p.photo_path) return { backgroundImage: `url(${p.photo_path})`, backgroundSize: 'cover', backgroundPosition: 'center', width: size, height: size, borderRadius: '50%' };
  let h = 0; const seed = `${p.first_name || ''}${p.last_name || ''}`;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return { width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.36, background: `linear-gradient(135deg, hsl(${h % 360} 70% 55%), hsl(${(h + 40) % 360} 70% 45%))` };
}
function initials(p) { return ((p.first_name || '')[0] || '') + ((p.last_name || '')[0] || ''); }
function monthShort(d) { return d ? new Date(d).toLocaleDateString('en-IN', { month: 'short' }) : ''; }
function dayOfMonth(d) { return d ? String(new Date(d).getDate()).padStart(2, '0') : ''; }
function holidayBg(cat) { return { national: 'rgba(239,68,68,0.12)', general: 'rgba(245,158,11,0.12)', restricted: 'rgba(148,163,184,0.18)', regional: 'rgba(59,143,244,0.12)' }[cat] || 'rgba(59,143,244,0.12)'; }
function holidayBorder(cat) { return { national: '#FCA5A5', general: '#FBC56B', restricted: '#CBD5E1', regional: '#8DC3FF' }[cat] || '#8DC3FF'; }

const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18 };
const balCard   = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 };
const sectionH  = { fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, marginBottom: 8 };
const emptyBox  = { padding: 20, textAlign: 'center', color: '#64748B', fontSize: 12.5, border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 8 };
const chipStyle = { fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, letterSpacing: '0.3px', textTransform: 'uppercase' };
const inputStyle = { padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%' };
const btnPrimary = { padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #FF00E5, #BF5FFF)', color: '#fff' };
const btnGhost   = { padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };
const btnApprove = { padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #10B981, #3B8FF4)', color: '#fff' };
// Distinct warm gradient reserved for WFH — visibly different from the
// primary leave button so employees never confuse the two.
const btnWFH     = {
  padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
  border: '1px solid rgba(251,191,36,0.55)', cursor: 'pointer',
  background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
  color: '#0B1220', letterSpacing: '0.3px',
  boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
};
const wfhTileStyle = {
  background: 'linear-gradient(135deg, rgba(251,191,36,0.10), rgba(245,158,11,0.06))',
  border: '1px solid rgba(251,191,36,0.35)',
  borderRadius: 10, padding: 12,
  boxShadow: '0 2px 10px rgba(245,158,11,0.10)',
};
const btnDanger  = { padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.12)', color: '#FCA5A5', cursor: 'pointer' };
const linkBtn    = { all: 'unset', cursor: 'pointer', color: '#60A5FA', fontSize: 12 };
const tblStyle   = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 };
